<template>
  <div class="ticket_view">
    <page-header :items="items">
      <template #buttons>
        <loading-button
          v-if="$can(`${$route.meta.permission}.edit`)"
          @click="updateTicket"
          :Loading="isLoading"
          :disabled="isFormInvalid"
        >
          <span>{{ $t("update") + " " + $t("ticket") }}</span>
        </loading-button>
      </template>
    </page-header>
    <validation-observer ref="simpleRules">
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <div class="card-right" v-if="isReportedCase">
                <ReportedCase :ticket="ticket" />
              </div>

              <div class="card-right mt-1">
                <div class="row mb-2">
                  <div class="col-md-6">
                    <div class="form-group">
                      <ElLabel
                        class="form-label"
                        for-label="title"
                        :title="$t('title')"
                        :is-required-icon="true"
                      />
                      <validation-provider
                        #default="{ errors }"
                        name="Title"
                        rules="required"
                      >
                        <input
                          id="title"
                          v-model="form.title"
                          name="title"
                          class="form-control"
                          type="text"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group" v-if="!!ticket.ticketNumber">
                      <label class="form-label">{{ $t("ticket") }}</label>
                      <div class="form-control disabled">
                        <p class="m-0">
                          {{ ticket.ticketNumber }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="form-label">{{ $t("created") }}</label>
                      <div class="form-control disabled">
                        <p v-if="ticket.createdAt" class="m-0">
                          {{
                            $dateFormatter(
                              formatTime(ticket.createdAt || ""),
                              $i18n.locale
                            )
                          }}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label class="input-label form-label" for="cars">{{
                        $t("company")
                      }}</label>
                      <MultiSelectInput
                        :class="'multiselect-zindex'"
                        :isDisabled="true"
                        v-model="companyId"
                        :options="customers"
                        :query="{ isMultiSelect: true }"
                        label="companyName"
                        trackBy="id"
                        :key="companyId"
                        moduleName="customers"
                        :multiple="false"
                        ><template #option="{ props }">
                          <div>
                            <p>
                              {{ props.option.companyNumber }} -
                              {{ props.option.companyName }}
                            </p>
                          </div>
                        </template>
                      </MultiSelectInput>
                    </div>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col-md-6">
                    <div class="form-group">
                      <ElLabel
                        class="form-label"
                        for-label="groups"
                        :title="$t('status')"
                      />
                      <select class="form-control" v-model="form.status">
                        <option value="new">
                          {{ $t("new") }}
                        </option>
                        <option value="open">
                          {{ $t("open") }}
                        </option>
                        <option value="waiting-for-response">
                          {{ $t("waitingForResponse") }}
                        </option>
                        <option value="pending">
                          {{ $t("pending") }}
                        </option>
                        <option value="resolved">
                          {{ $t("resolved") }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <ElLabel
                        class="form-label"
                        for-label="groups"
                        :title="$t('priority')"
                      />
                      <select class="form-control" v-model="form.priority">
                        <option value="low">{{ $t("low") }}</option>
                        <option value="normal">{{ $t("normal") }}</option>
                        <option value="high">{{ $t("high") }}</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div class="row mb-1">
                  <div class="col-12 col-md-6">
                    <div class="form-group m-0">
                      <ElLabel
                        class="form-label"
                        for-label="groups"
                        :title="$t('reporter')"
                      />
                      <div
                        v-if="!!ticket.userId"
                        class="d-flex align-items-center gap-2"
                      >
                        <img
                          v-if="
                            userProfiles[ticket.userId] &&
                            userProfiles[ticket.userId].profile_image
                          "
                          :src="userProfiles[ticket.userId].profile_image"
                          alt=""
                          style="
                            width: 35px;
                            height: 35px;
                            object-fit: cover;
                            border-radius: 50%;
                          "
                        />
                        <h5 v-if="userProfiles[ticket.userId]" class="ms-1">
                          {{
                            (userProfiles[ticket.userId].first_name || "") +
                            " " +
                            (userProfiles[ticket.userId].last_name || "")
                          }}
                        </h5>
                      </div>
                      <div v-else class="d-flex align-items-center">
                        <img
                          src="../../assets/images/default.png"
                          style="
                            width: 35px;
                            height: 35px;
                            object-fit: cover;
                            border-radius: 50%;
                          "
                        />
                        <h5 class="ms-1"></h5>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-right mt-1">
                <div
                  v-if="$can(`backoffice-ticket-comment.create`)"
                  class="add_comm mb-3"
                  id="Comment"
                >
                  <b-tabs v-model="commentType">
                    <b-tab :title="$t('internal')"></b-tab>
                    <b-tab :title="$t('public')"></b-tab>
                  </b-tabs>
                  <div
                    class="form-group m-0 editor"
                    v-show="ticket.state !== 'resolved'"
                  >
                    <MarkdownEditor
                      :content="commentText || ''"
                      @change="commentText = $event"
                      :builder-images="[]"
                      :editable="true"
                      @imageAdded="addCommentImages"
                    />
                  </div>
                  <div v-if="hasCommentError">
                    <span class="text-danger ms-1"
                      >{{ $t("thisValueIsRequired") }}. {{ $t("minimum40CharactersRequired") }}.</span
                    >
                  </div>
                  <div class="dropzone-wrapper mt-2" v-if="enableAttachment">
                    <div class="dropzone" v-show="ticket.state !== 'resolved'">
                      <vue-dropzone
                        id="dropzone"
                        ref="myDropzone"
                        :disabled="ticket.state === 'resolved' || false"
                        :options="dropzoneOptions"
                      />
                    </div>
                    <show-files
                      :commentFiles="commentFiles"
                      @remove="deleteUpload"
                    />
                  </div>
                  <div
                    class="add_bottom d-flex mt-2"
                    :class="{
                      'justify-content-end': ticket.state === 'resolved',
                    }"
                  >
                    <div
                      class="d-flex align-items-center gap-2"
                      v-if="ticket.state !== 'resolved'"
                    >
                      <button
                        :title="ticket.state === 'resolved' ? 'disabled' : ''"
                        @click="createComment"
                        :disabled="ticket.state === 'resolved' || !commentBtn || isTicketCommentTextInvalid"
                        class="btn btn-primary waves-effect waves-light"
                      >
                        {{
                          $t(
                            `create${
                              commentType == 0 ? "InternalNote" : "PublicAnswer"
                            }`
                          )
                        }}
                      </button>
                      <div
                        :title="ticket.state === 'resolved' ? 'disabled' : ''"
                      >
                        <label
                          for="ticket-file"
                          class="btn btn-primary waves-effect waves-light custom-file-upload"
                        >
                          <i
                            class="fas fa-cloud-upload-alt text-white"
                            style="font-size: 15px"
                          ></i>
                          <span class="ms-1">{{ $t("addAttachment") }}</span>
                        </label>
                        <button
                          id="ticket-file"
                          class="form-control"
                          :disabled="ticket.state === 'resolved'"
                          @click="openDropZone()"
                          style="display: none"
                        ></button>
                      </div>
                    </div>
                  </div>
                </div>
                <template
                  v-if="
                    comments &&
                    comments.length > 0 &&
                    $can(`backoffice-ticket-comment.list`)
                  "
                >
                  <div
                    v-for="comment in comments || []"
                    :key="'comment-' + comment.id"
                  >
                    <div :id="'comment-' + comment.id" :class="['comment']">
                      <div class="comment-header">
                        <div class="comment-user">
                          <img
                            :title="comment.user.email || ''"
                            v-if="
                              !!userProfiles[comment.userId] &&
                              !!userProfiles[comment.userId].profile_image
                            "
                            :src="userProfiles[comment.userId].profile_image"
                            class="comment-user-img"
                          />
                          <img
                            v-else
                            :title="
                              (userProfiles[comment.userId] &&
                                userProfiles[comment.userId].email) ||
                              ''
                            "
                            src="../../assets/images/default.png"
                            class="comment-user-img"
                          />
                          <div class="comment-user-title">
                            <h3
                              v-if="
                                userProfiles[comment.userId] &&
                                (userProfiles[comment.userId].first_name ||
                                  userProfiles[comment.userId].last_name)
                              "
                            >
                              {{
                                (userProfiles[comment.userId].first_name ||
                                  "") +
                                " " +
                                (userProfiles[comment.userId].last_name || "")
                              }}
                            </h3>
                            <h3 v-else>{{ $t("noName") }}</h3>
                          </div>
                        </div>
                        <div
                          v-if="
                            user &&
                            comment &&
                            ticket &&
                            user.id === comment.userId &&
                            comment.isArchived === 0 &&
                            ticket.isArchived === 0
                          "
                          class="dropdown"
                        >
                          <i
                            class="hidden cursor-pointer mt-5 mr-2 fa-solid fa-ellipsis-vertical"
                          ></i>
                          <div class="dropdown-content">
                            <p
                              class="pt-2 pl-2 pb-2 cursor-pointer dropdown-action"
                            >
                              {{ $t("edit") }}
                            </p>
                            <hr />
                            <p
                              class="pt-2 pl-2 pb-2 cursor-pointer dropdown-action"
                            >
                              {{ $t("delete") }}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div class="comment-body">
                        <div class="comment-text preview-mode">
                          <div
                            :class="{
                              'is-archived': comment.isArchived == 1,
                            }"
                            :id="'editor-' + comment.id"
                            class="editor-container"
                          >
                            <MarkdownEditor
                              v-if="renderedEditors.has(comment.id)"
                              :content="comment.text || ''"
                              :builder-images="comment.images || []"
                              :editable="false"
                              :key="comment.id"
                            />
                          </div>

                          <p
                            v-if="errorTicketCommentId == comment.id"
                            style="color: red"
                          >
                            {{ $t("thisFormIsRequiredToBeFilled") }}
                          </p>
                          <div
                            class="d-flex align-items-center justify-content-between w-100 mt-2"
                          >
                            <p>
                              <span v-if="comment.createdAt">
                                {{
                                  $dateFormatter(
                                    formatTime(comment.createdAt || ""),
                                    $i18n.locale
                                  )
                                }}
                              </span>
                              <span v-else>
                                {{ $t("accountedTime") }}:
                                {{ $t("notSet") }}
                              </span>
                              {{
                                comment.isArchived === 1
                                  ? " - " + $t("isDeleted")
                                  : ""
                              }}
                            </p>
                          </div>
                        </div>
                      </div>
                      <CommentFooter
                        v-if="
                          comment.attachments && comment.attachments.length > 0
                        "
                        :attachments="comment.attachments"
                      />
                    </div>
                  </div>
                </template>
                <p
                  v-if="
                    (comments.length || 0) < ticketCommentsTotal &&
                    $can('bacloffice-ticket-comment.list')
                  "
                  class="text-align-center"
                  ref="load"
                >
                  {{ $t("loadingMore") }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </validation-observer>
    <!-- <div class="img-prev-modal" v-if="isPrevShow">
      <div class="modal-content">
        <img class="img-prev" :src="isImgPreview" alt="" />
        <div class="close-modal" @click="closeImgPrev">
          <feather-icon size="18" icon="XIcon" />
        </div>
      </div>
    </div> -->
  </div>
</template>

<script>
import { Quill } from "vue-quill-editor/src";
import PageHeader from "@/components/PageHeader.vue";
import ElLabel from "@/components/ElLabel.vue";
import vueDropzone from "vue2-dropzone";
import "@/assets/scss/pages/_tickets.scss";
import ShowFiles from "@/components/ticketSystem/show-files.vue";
import LoadingButton from "@/components/LoadingButton.vue";
import MarkdownEditor from "@/components/MarkdownEditor.vue";
import { mapGetters } from "vuex";
import TextInput from "@/components/TextInput.vue";
import ReportedCase from "@/components/ticketSystem/ReportedCase.vue";
import CommentFooter from "@/components/ticketSystem/CommentFooter.vue";
import MultiSelectInput from "@/components/MultiSelectInput.vue";
import usersStoreMixin from "@/mixins/usersStoreMixin";
import fileHandlerMixin from "@/mixins/fileHandlerMixin";
import ElementsIcon from "@/components/elements/Icon.vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, email } from "@validations";
import formDirtyMixin from "@/mixins/formDirtyMixin";

export default {
  mixins: [usersStoreMixin, fileHandlerMixin, formDirtyMixin],
  components: {
    LoadingButton,
    ShowFiles,
    CommentFooter,
    ElLabel,
    PageHeader,
    vueDropzone,
    TextInput,
    ReportedCase,
    MarkdownEditor,
    MultiSelectInput,
    ElementsIcon,
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      originalForm: null,
      ticketCommentsTotal: 0,
      errorTicketCommentId: null,
      commentType: 0,
      title: "",
      submitted: false,
      commentText: "",
      commentBtn: true,
      hasCommentError: false,
      observer: new IntersectionObserver(this.infiniteScroll),
      fileObserver: new IntersectionObserver(this.loadCommentFiles),
      editorObserver: new IntersectionObserver(this.handleEditorVisibility),
      visibleEditors: new Set(),
      loadedImages: new Map(),
      renderedEditors: new Set(),
      perPage: 10,
      companyId: "",
      ticket: {
        title: "",
        state: "",
        priority: "",
        visibility: "",
        createdAt: "",
      },
      form: {
        title: "",
        priority: "",
        createdAt: "",
      },
      isLoading: false,
      enableAttachment: false,
      commentFile: "",
      commentFiles: [],
      comments: [],
      isCloseTicket: true,
      isReportedCase: false,
      images: [],
      loadedCommentIds: new Set(),
    };
  },
  computed: {
    ...mapGetters(["showLoader"]),
    ...mapGetters("tickets", ["tickets"]),
    ...mapGetters("ticketComments", []),
    ...mapGetters("auth", ["user"]),
    ...mapGetters("users", ["userProfiles"]),
    ...mapGetters("customers", ["customers"]),
    items() {
      return [
        {
          text: this.$t("dentalTwin"),
          to: "/",
        },
        {
          text: this.$t("Tickets"),
          to: "/tickets",
        },
        {
          text: this.$t("edit"),
          active: true,
        },
      ];
    },

    dropzoneOptions() {
      const component = this; // Capture the component context
      return {
        url: process.env.backEndUrl + "/api/convert-file-to-base64",
        autoProcessQueue: false,
        maxFiles: 5,
        accept: function (file, done) {
          done();
        },
        acceptedFiles:
          ".jpg,.jpeg,.png,.pdf,.csv,.doc,.docx,.ppt,.pptx,.xls,.xlsx",
        dictDefaultMessage: this.$t("clickOrDragFile"),
        addRemoveLinks: true,
        init: function () {
          this.on("addedfile", function (file) {
            component.processFile(file); // Use the component context
          });
        },
      };
    },
    isFormInvalid() {
      return !this.form.title;
    },
    isTicketCommentTextInvalid() {
      return !this.commentText || this.commentText.length < 40;
    },
  },
  watch: {
    form: {
      handler(newVal) {
        this.isFormDirty = JSON.stringify(newVal) !== this.originalForm;
      },
      deep: true,
    },
  },
  methods: {
    updateDropzoneMessage() {
      if (this.$refs.myDropzone && this.$refs.myDropzone.dropzone) {
        const dropzoneInstance = this.$refs.myDropzone.dropzone;
        dropzoneInstance.options.dictDefaultMessage =
          this.$t("clickOrDragFile");
        dropzoneInstance.element.querySelector(".dz-message").textContent =
          this.$t("clickOrDragFile");
      }
    },
    addCommentImages(event) {
      this.images = event;
    },
    customLabel(props) {
      return `${props?.first_name || ""} ${props?.last_name || ""}`;
    },

    async loadCommentFiles(entries) {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const commentId = entry.target.id.split("-").slice(1).join("-");
          if (!this.loadedCommentIds.has(commentId)) {
            const comment = this.comments.find((c) => c.id === commentId);
            if (comment) {
              // Load images if any
              if (comment.images && comment.images.length > 0) {
                let loadedCount = 0;
                for (const image of comment.images) {
                  if (!image.base64) {
                    try {
                      const fileResponse = await this.handleFileDownload(
                        image.id
                      );
                      if (fileResponse?.data) {
                        // Convert blob to base64
                        const reader = new FileReader();
                        reader.onload = () => {
                          image.base64 = reader.result;
                          loadedCount++;
                          // Check if all images are loaded
                          if (loadedCount === comment.images.length) {
                            this.loadedImages.set(commentId, true);
                            this.renderedEditors.add(commentId);
                            // Force re-render by updating the comment
                            const commentIndex = this.comments.findIndex(
                              (c) => c.id === commentId
                            );
                            if (commentIndex !== -1) {
                              this.$set(this.comments, commentIndex, {
                                ...comment,
                              });
                            }
                          }
                        };
                        reader.readAsDataURL(fileResponse.data);
                      }
                    } catch (error) {
                      console.error("Error retrieving comment image:", error);
                    }
                  } else {
                    loadedCount++;
                    if (loadedCount === comment.images.length) {
                      this.loadedImages.set(commentId, true);
                      this.renderedEditors.add(commentId);
                    }
                  }
                }
              } else {
                // If no images, mark as loaded and force re-render immediately
                this.loadedImages.set(commentId, true);
                this.renderedEditors.add(commentId);
                // Force re-render by updating the comment
                const commentIndex = this.comments.findIndex(
                  (c) => c.id === commentId
                );
                if (commentIndex !== -1) {
                  this.$set(this.comments, commentIndex, {
                    ...comment,
                  });
                }
              }

              this.loadedCommentIds.add(commentId);
            }
          }
        }
      }
    },

    async assignDataComments(response) {
      if (!!response) {
        this.ticketCommentsTotal = response?.length ?? 0;
        this.comments = response ?? [];

        this.$nextTick(() => {
          this.comments.forEach((comment) => {
            const element = document.getElementById(`comment-${comment.id}`);
            if (element) {
              this.fileObserver.observe(element);
            }
            const editorElement = document.getElementById(
              `editor-${comment.id}`
            );
            if (editorElement) {
              this.editorObserver.observe(editorElement);
            }
          });
        });

        await this.getUserProfiles(
          this.comments.map((comment) => comment.userId)
        );
      }
    },

    async infiniteScroll([{ isIntersecting, target }]) {
      if (isIntersecting) {
        const ul = target.offsetParent;
        const scrollTop = target.offsetParent.scrollTop;
        this.perPage += 10;
        if (this.$can("backoffice-ticket-comment.list")) {
          const responseComments = await this.$store.dispatch(
            "ticketComments/list",
            {
              params: {
                ticketId: this.$route.params.id,
                perPage: this.perPage,
                sortOrder: "desc",
                sortBy: "created_at",
              },
            }
          );

          await this.assignDataComments(responseComments?.data?.data);
        }
        await this.$nextTick();
        ul.scrollTop = scrollTop;
      }
    },
    async loadItems() {
      try {
        this.$store.commit("showLoader", true);
        const response = await this.$store.dispatch(
          "tickets/show",
          this.$route.params.id
        );

        this.ticket = response?.data?.data ?? {};
        this.form.priority = this.ticket.priority;
        this.form.status = this.ticket.status;
        this.form.dueDate = this.ticket.dueDate;
        this.form.title = this.ticket.title;
        this.originalForm = JSON.stringify(this.form);
        this.companyId = this.ticket.company ?? null;
        if (this.ticket && this.ticket.case) this.isReportedCase = true;

        if (this.$can("backoffice-ticket-comment.list")) {
          const responseComments = await this.$store.dispatch(
            "ticketComments/list",
            {
              params: {
                perPage: this.perPage,
                sortBy: "created_at",
                sortOrder: "desc",
                ticketId: this.$route.params.id,
              },
            }
          );
          await this.assignDataComments(responseComments?.data?.data);
        }
        const usersToBeFetched = [this.ticket.userId];
        await this.getUserProfiles(usersToBeFetched);
      } catch (e) {
        console.log(e);
      } finally {
        this.$store.commit("showLoader", false);
      }
    },
    formatTime(timestamp) {
      const utcDate = new Date(timestamp);
      const offset = utcDate.getTimezoneOffset() * 60000; // Offset in milliseconds
      const localTimestamp = utcDate.getTime() - offset;
      const localDate = new Date(localTimestamp);
      return localDate.toLocaleString();
    },
    htmlFromText(text) {
      try {
        let article = document.createElement("article");
        let quill = new Quill(article);
        quill.setContents(JSON.parse(text));
        return quill.root.innerHTML;
      } catch (err) {
        return md.render(text);
      }
    },
    getInitials(name) {
      const tokens = name?.split(" ");
      if (tokens)
        return `${tokens?.[0]?.[0] ?? ""}${
          tokens?.[1]?.[0] ?? ""
        }`.toUpperCase();
      else return "";
    },
    processFile(file) {
      const fileData = {
        file: file,
        name: file.name,
        type: file.name.substring(file.name.lastIndexOf(".") + 1).toLowerCase(),
        size: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
      };
      this.commentFiles.push(fileData);
    },
    async createComment(baypass = true) {
      try {
        if (this.commentText == "" || this.commentText == null || this.commentText.length < 40) {
          this.hasCommentError = true;
          return false;
        }
        let attachments = [];
        this.commentBtn = false;
        if (!!this.commentFiles)
          attachments = Array.from(new Set(this.commentFiles));

        this.isCloseTicket = baypass;
        this.$store.commit("showLoader", true);

        let hasUploadError = false;
        // Upload all pending files
        const uploadedFiles = [];
        for (const fileData of attachments) {
          try {
            const response = await this.handleFileUpload(fileData.file);
            if (!response?.data[0]?.id) {
              hasUploadError = true;
              break;
            }
            uploadedFiles.push({
              id: response?.data[0]?.id,
              name: fileData.name,
              extension: fileData.type,
              size: fileData.size,
            });
          } catch (error) {
            console.error("Error uploading file:", error);
            hasUploadError = true;
            break;
          }
        }

        // Upload markdown images if any
        let uploadedImages = [];
        if (!hasUploadError && this.images && this.images.length > 0) {
          for (const image of this.images) {
            try {
              // Convert base64 to File object
              const file = this.base64ToFile(image.base64, image.name);
              const response = await this.handleFileUpload(file);
              if (!response?.data[0]?.id) {
                hasUploadError = true;
                break;
              }
              uploadedImages.push({
                id: response?.data[0]?.id,
                name: image.name,
                rects: image.rects,
                position: image.position,
                extension: response?.data[0]?.type,
              });
            } catch (error) {
              console.error("Error uploading markdown image:", error);
              hasUploadError = true;
              break;
            }
          }
        }

        if (hasUploadError) {
          this.$toast.error(
            this.$t("errorUploadingFilesPleaseTryAgain")
          );
          this.commentBtn = true;
          this.$store.commit("showLoader", false);
          return;
        }

        await this.$store
          .dispatch("ticketComments/create", {
            text: this.commentText,
            images: uploadedImages,
            ticketId: this.$route.params.id,
            visibility: this.commentType == 0 ? "internal" : "public",
            attachments: uploadedFiles,
            userId: localStorage.getItem("user_id"),
          })
          .then(async (response) => {
            await this.loadItems();
            this.commentText = "";
            this.commentFiles = [];
            this.images = [];
          })
          .catch((error) => {
            this.commentText = "";
            this.commentFiles = [];
            this.images = [];
            console.log("error", error);
          })
          .finally(() => {
            this.$store.commit("showLoader", false);
          });
      } catch (e) {
        console.log(e);
      } finally {
        this.$store.commit("showLoader", false);
        this.commentBtn = true;
        this.hasCommentError = false;
      }
    },
    ready() {
      try {
        this.$refs.quillEditor.setContents(JSON.parse(this.comment));
      } catch (e) {
        this.$refs.quillEditor.setContents(this.comment ?? "");
      }
    },
    deleteUpload(index) {
      this.commentFiles.splice(index, 1);
    },
    base64ToFile(base64String, filename) {
      // Split the base64 string to get the content type and the actual data
      const arr = base64String.split(",");
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);

      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }

      return new File([u8arr], filename, { type: mime });
    },
    openDropZone() {
      this.enableAttachment = true;
    },
    openImgPrev() {
      this.isPrevShow = true;
    },
    closeImgPrev() {
      this.isPrevShow = false;
    },
    async updateTicket() {
      this.$store.commit("showLoader", true);
      this.submitted = true;
      try {
        this.$refs.simpleRules.validate().then(async (success) => {
          if (success) {
            await this.$store
              .dispatch("tickets/update", {
                id: this.$route.params.id,
                data: {
                  ...this.form,
                },
              })
              .then(async (response) => {
                this.isFormDirty = false;
                this.originalForm = JSON.stringify(this.form);
                this.$router.push("/tickets");
              });
          }
        });
      } catch (e) {
        console.log(e);
      } finally {
        this.$store.commit("showLoader", false);
        this.submitted = false;
      }
    },
    handleEditorVisibility(entries) {
      entries.forEach((entry) => {
        const commentId = entry.target.id.split("-").slice(1).join("-");
        if (entry.isIntersecting) {
          this.visibleEditors.add(commentId);
          // If images are already loaded, add to rendered editors
          if (this.loadedImages.has(commentId)) {
            this.renderedEditors.add(commentId);
          }
        }
      });
    },
  },
  async mounted() {
    this.updateDropzoneMessage(); // Set initial message
    this.$watch("$i18n.locale", () => {
      this.updateDropzoneMessage(); // Update message when language changes
    });
    await this.$store.dispatch("customers/list", { isMultiSelect: true });
    await this.loadItems();
  },
  watch: {
    ticketCommentsTotal() {
      this.$nextTick(() => {
        if (this.$refs.load) {
          this.observer?.observe(this.$refs.load);
        }
      });
    },
  },
  beforeDestroy() {
    this.observer?.disconnect();
    this.fileObserver?.disconnect();
    this.editorObserver?.disconnect();
  },
};
</script>

<style lang="scss">
body.dark-layout
  .preview-mode
  .mavonEditor
  .v-note-wrapper
  .v-note-show
  .v-show-content {
  border: none !important;
  background: transparent !important;
}
.preview-mode .markdown-body {
  border: none !important;
  background: transparent !important;
}
.preview-mode {
  .mavonEditor .v-note-wrapper .v-note-show .v-show-content {
    background: transparent !important;
  }
}
.preview-mode .v-note-op {
  display: none !important;
}
.preview-mode .v-note-edit {
  display: none !important;
}
.preview-mode .v-note-panel {
  display: block !important;
}
.preview-mode .v-show-content {
  overflow: auto !important;
  background-color: white !important;
  padding: 0 !important;
}
body.dark-layout .preview-mode .v-show-content {
  background-color: #091420 !important;
  color: #fff;
}
.preview-mode .v-note-show {
  width: 100% !important;
}
.preview-mode .v-note-wrapper {
  z-index: 1;
  min-height: auto;
  padding: 0;
}

.preview-mode .v-note-wrapper p {
  margin-bottom: 0;
}

.preview-mode .markdown-body {
  height: auto;
  box-shadow: none !important;
  background-color: transparent !important;
}
</style>
